
import React from 'react';
import { useTranslation } from 'react-i18next';
import { getSignEmoji } from '../../utils/homeHelpers';

const modalBackdropStyle = {
  position: 'fixed',
  inset: 0,
  zIndex: 10080,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 'calc(env(safe-area-inset-top, 0px) + 12px 12px calc(env(safe-area-inset-bottom, 0px) + 12px)',
  background: 'rgba(8, 0, 18, 0.46)',
  backdropFilter: 'blur(8px)',
  WebkitBackdropFilter: 'blur(8px)',
};

const sheetStyle = {
  width: 'min(430px, 100%)',
  maxHeight: 'min(82dvh, calc(100dvh - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px) - 24px), 680px)',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  borderRadius: 24,
  border: '1px solid rgba(255, 215, 0, 0.24)',
  background:
    'radial-gradient(circle at 18% 0%, rgba(255,215,0,.17), transparent 34%), linear-gradient(145deg, rgba(27, 8, 50, .98), rgba(69, 0, 122, .94))',
  boxShadow: '0 -20px 48px rgba(0,0,0,.38), inset 0 1px 0 rgba(255,255,255,.12)',
  color: '#fff',
  WebkitOverflowScrolling: 'touch',
  minHeight: 0,
};

const HoroscopeCard = ({ horoscope, user, horoOpen, setHoroOpen, getZodiacSignLabel }) => {
  const { t, i18n } = useTranslation('home');
  const isEnglish = String(i18n.resolvedLanguage || i18n.language || '').toLowerCase().startsWith('en');
  const horoscopeMessage = String(horoscope?.message || '').trim();
  const horoscopeReady = horoscopeMessage.length > 0;
  const resolvedSignLabel = horoscope?.sign
    ? String(horoscope.sign)
    : (user?.zodiacSign ? String(user.zodiacSign) : t('horoscope.yourSign'));

  const normalizedHoroscopeMessage = horoscopeMessage.replace(/\s+/g, ' ').trim();
  const horoscopeParts = horoscope?.parts && typeof horoscope.parts === 'object' ? horoscope.parts : null;

  const ritualMatch = normalizedHoroscopeMessage.match(/Ritual:\s*([^.!?]+[.!?]?)/i);
  const ritualText = horoscopeParts?.ritual || (ritualMatch
    ? ritualMatch[1]
        .replace(/\s+/g, ' ')
        .replace(/[.!?]+$/, '')
        .trim()
    : '');

  const messageWithoutRitual = normalizedHoroscopeMessage
    .replace(/Ritual:\s*.+$/i, '')
    .trim();

  const cleanSentences = messageWithoutRitual
    .split(/(?<=[.!?])\s+/)
    .map((s) => s.trim())
    .filter(Boolean);

  const mainText = (horoscopeParts?.main || cleanSentences[0] || normalizedHoroscopeMessage)
    .replace(/\s*\([^)]*\)\s*/g, ' ')
    .replace(/\s+([.,;:!?])/g, '$1')
    .replace(/\s+/g, ' ')
    .trim();

  const actionText =
    horoscopeParts?.bestAction ||
    cleanSentences.find((s) =>
      (isEnglish
        ? /choose|avoid|organize|release|trust|define|look|listen|prioritize|ask|pause|return|close|say no/i
        : /pedí|evitá|ordená|soltá|elegí|confiá|definí|mirá|escuchá|priorizá/i
      ).test(s) &&
      s !== mainText
    ) ||
    cleanSentences[1] ||
    '';

  const rememberText = horoscopeParts?.reminder || '';
  const dominantEnergyText = horoscopeParts?.dominantEnergy || '';
  const dailyChallengeText = horoscopeParts?.challenge || '';

  const vibrationText = horoscopeParts?.vibration || '';
  const cosmicStateText = horoscopeParts?.cosmicState || '';
  const universeAdviceText = horoscopeParts?.universeAdvice || '';

  const translatedSign = typeof getZodiacSignLabel === 'function'
    ? getZodiacSignLabel(resolvedSignLabel)
    : resolvedSignLabel;
  const formattedSign = translatedSign.charAt(0).toUpperCase() + translatedSign.slice(1);

  return (
    <>
      <button
        type="button"
        className={`horo-card ${horoscopeReady ? 'is-ready' : 'is-loading'}`}
        onClick={() => {
          if (!horoscopeReady) return;
          setHoroOpen(true);
        }}
        aria-label={t('horoscope.openAria')}
        aria-expanded={!!(horoOpen && horoscopeReady)}
        aria-disabled={!horoscopeReady}
        style={{
          width: '100%',
          minHeight: 112,
          border: '1px solid rgba(218,165,32,.34)',
          borderRadius: 22,
          padding: '14px 14px 13px',
          display: 'grid',
          gridTemplateColumns: '48px 1fr auto',
          alignItems: 'center',
          gap: 12,
          cursor: horoscopeReady ? 'pointer' : 'default',
          color: '#fff',
          background:
            'linear-gradient(145deg, rgba(14,4,34,.84), rgba(25,6,54,.62)), radial-gradient(circle at 14% 0%, rgba(255,215,0,.14), transparent 34%), radial-gradient(circle at 96% 24%, rgba(183,44,255,.14), transparent 36%)',
          boxShadow:
            '0 16px 36px rgba(0,0,0,.30), inset 0 1px 0 rgba(255,255,255,.08)',
          textAlign: 'left',
          opacity: 1,
          overflow: 'hidden',
          position: 'relative',
          transition: 'opacity 160ms ease, transform 160ms ease, border-color 160ms ease',
        }}
      >
        <span
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            background:
              'linear-gradient(135deg, rgba(255,255,255,.08), transparent 38%), radial-gradient(circle at 85% 90%, rgba(255,215,0,.12), transparent 30%)',
            pointerEvents: 'none',
          }}
        />

        <span
          aria-hidden="true"
          style={{
            position: 'relative',
            zIndex: 1,
            width: 48,
            height: 48,
            borderRadius: '50%',
            display: 'grid',
            placeItems: 'center',
            background:
              'radial-gradient(circle at 35% 25%, rgba(255,255,255,.55), transparent 28%), linear-gradient(145deg, rgba(255,215,0,.96), rgba(138,43,226,.82))',
            color: '#140027',
            boxShadow: '0 0 24px rgba(218,165,32,.28), inset 0 1px 0 rgba(255,255,255,.36)',
            fontSize: 23,
          }}
        >
          🔮
        </span>

        <span style={{ position: 'relative', zIndex: 1, minWidth: 0 }}>
          <span
            style={{
              display: 'block',
              color: '#F3C96B',
              fontSize: '.72rem',
              fontWeight: 950,
              letterSpacing: '.18em',
              textTransform: 'uppercase',
              marginBottom: 5,
              whiteSpace: 'nowrap',
            }}
          >
            {t('horoscope.cardKicker')}
          </span>

          <strong
            style={{
              display: 'block',
              color: '#fff',
              fontSize: '1.03rem',
              lineHeight: 1.15,
              fontWeight: 950,
              marginBottom: 7,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {horoscopeReady ? t('horoscope.ready') : t('horoscope.loading')}
          </strong>

          <span
            style={{
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              color: 'rgba(255,255,255,.72)',
              fontSize: '.82rem',
              lineHeight: 1.35,
            }}
          >
            {horoscopeReady
              ? (mainText || t('horoscope.readyDescription', { sign: formattedSign }))
              : t('horoscope.loadingDescription')}
          </span>
        </span>

        <span
          style={{
            position: 'relative',
            zIndex: 1,
            display: 'grid',
            justifyItems: 'end',
            alignSelf: 'stretch',
            gap: 8,
          }}
        >
          <span
            style={{
              borderRadius: 999,
              padding: '5px 8px',
              color: '#FFD700',
              background: 'rgba(255,255,255,.08)',
              border: '1px solid rgba(255,255,255,.10)',
              fontSize: '.68rem',
              fontWeight: 900,
              whiteSpace: 'nowrap',
            }}
          >
            {getSignEmoji(resolvedSignLabel)} {formattedSign}
          </span>

          <span
            style={{
              alignSelf: 'end',
              borderRadius: 999,
              padding: '8px 10px',
              background: horoscopeReady
                ? 'linear-gradient(135deg,#8A2BE2,#DAA520)'
                : 'rgba(255,255,255,.10)',
              color: horoscopeReady ? '#fff' : 'rgba(255,255,255,.62)',
              fontSize: '.72rem',
              fontWeight: 950,
              letterSpacing: '.06em',
              whiteSpace: 'nowrap',
              boxShadow: horoscopeReady ? '0 10px 18px rgba(0,0,0,.24)' : 'none',
            }}
          >
            {t('horoscope.view')}
          </span>
        </span>
      </button>

      {horoOpen && horoscopeReady && (
        <div
          role="presentation"
          style={modalBackdropStyle}
          onClick={() => setHoroOpen(false)}
        >
          <section
            role="dialog"
            aria-modal="true"
            aria-label={t('horoscope.dialogAria')}
            style={sheetStyle}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{
                position: 'relative',
                flex: '0 0 auto',
                zIndex: 1,
                padding: '10px 14px 9px',
                background: 'linear-gradient(180deg, rgba(43, 8, 76, .98), rgba(43, 8, 76, .88))',
                borderBottom: '1px solid rgba(255,255,255,.08)',
              }}
            >
              <div
                aria-hidden="true"
                style={{
                  width: 42,
                  height: 4,
                  borderRadius: 999,
                  margin: '0 auto 10px',
                  background: 'rgba(255,255,255,.26)',
                }}
              />

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                <div>
                  <span
                    style={{
                      display: 'block',
                      color: '#FFD700',
                      fontSize: '.72rem',
                      fontWeight: 950,
                      textTransform: 'uppercase',
                      letterSpacing: '.10em',
                    }}
                  >
                    {t('horoscope.dailyEnergy')}
                  </span>
                  <h2 style={{ margin: '3px 0 0', fontSize: '1.2rem', lineHeight: 1.05 }}>
                    {getSignEmoji(resolvedSignLabel)} {t('horoscope.title', { sign: formattedSign })}
                  </h2>
                </div>

                <button
                  type="button"
                  onClick={() => setHoroOpen(false)}
                  aria-label={t('horoscope.closeAria')}
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    border: '1px solid rgba(255,255,255,.14)',
                    background: 'rgba(255,255,255,.09)',
                    color: '#fff',
                    fontSize: 20,
                    fontWeight: 900,
                    cursor: 'pointer',
                    display: 'grid',
                    placeItems: 'center',
                    lineHeight: 1,
                    padding: 0,
                    flex: '0 0 auto',
                  }}
                >
                  ×
                </button>
              </div>
            </div>

            <div
              style={{
                padding: '14px 15px calc(env(safe-area-inset-bottom, 0px) + 18px)',
                overflowY: 'auto',
                WebkitOverflowScrolling: 'touch',
                minHeight: 0,
                flex: '1 1 auto',
              }}
            >
              <div className="horo-reading-premium">
                <div className="horo-energy-headline">
                  <span className="horo-energy-kicker">{t('horoscope.mainEnergy')}</span>
                  <p className="horo-main-message">{mainText}</p>
                </div>

                {vibrationText ? (
                  <div className="horo-vibe-meter" aria-label={t('horoscope.vibrationAria', { value: vibrationText })}>
                    <span className="horo-vibe-label">{t('horoscope.vibration')}</span>
                    <span className="horo-vibe-track" aria-hidden="true">
                      <span className="horo-vibe-fill" />
                    </span>
                    <span className="horo-vibe-value">{vibrationText}</span>
                  </div>
                ) : null}

                <div className="horo-grid">
                  {cosmicStateText ? <div className="horo-mini-block horo-mini-block-cosmic">
                    <span className="horo-mini-block-label">{t('horoscope.cosmicState')}</span>
                    <span className="horo-mini-block-text">{cosmicStateText}</span>
                  </div> : null}

                  {dominantEnergyText ? <div className="horo-mini-block horo-mini-block-dominant">
                    <span className="horo-mini-block-label">{t('horoscope.dominantEnergy')}</span>
                    <span className="horo-mini-block-text">{dominantEnergyText}</span>
                  </div> : null}

                  {dailyChallengeText ? <div className="horo-mini-block horo-mini-block-challenge">
                    <span className="horo-mini-block-label">{t('horoscope.challenge')}</span>
                    <span className="horo-mini-block-text">{dailyChallengeText}</span>
                  </div> : null}

                  {actionText ? (
                    <div className="horo-mini-block horo-mini-block-full horo-mini-block-action">
                      <span className="horo-mini-block-label">{t('horoscope.bestAction')}</span>
                      <span className="horo-mini-block-text">{actionText}</span>
                    </div>
                  ) : null}

                  {ritualText ? (
                    <div className="horo-mini-block horo-mini-block-full horo-mini-block-ritual">
                      <span className="horo-mini-block-label">{t('horoscope.ritual')}</span>
                      <span className="horo-mini-block-text">
                        {ritualText.charAt(0).toUpperCase() + ritualText.slice(1)}
                      </span>
                    </div>
                  ) : null}

                  {universeAdviceText ? <div className="horo-mini-block horo-mini-block-full horo-mini-block-universe">
                    <span className="horo-mini-block-label">{t('horoscope.universeAdvice')}</span>
                    <span className="horo-mini-block-text">{universeAdviceText}</span>
                  </div> : null}

                  {rememberText ? <div className="horo-mini-block horo-mini-block-full horo-mini-block-remember">
                    <span className="horo-mini-block-label">{t('horoscope.reminder')}</span>
                    <span className="horo-mini-block-text">{rememberText}</span>
                  </div> : null}
                </div>
              </div>
            </div>
          </section>
        </div>
      )}
    </>
  );
};

export default HoroscopeCard;
